import React, { useState } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";

import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";

import "./ScentworkRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun;
}

const LEVELS = [
  { value: 'Novice A', label: 'Novice A' },
  { value: 'Novice B', label: 'Novice B' },
  { value: 'Advanced', label: 'Advanced' },
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Master', label: 'Master' },
  { value: 'Detective', label: 'Detective' }
];

const ELEMENTS = [
  { value: 'Container', label: 'Container' },
  { value: 'Interior', label: 'Interior' },
  { value: 'Exterior', label: 'Exterior' },
  { value: 'Buried', label: 'Buried' },
  { value: 'Handler Discrimination', label: 'Handler Discrimination' },
  { value: 'Detective', label: 'Detective' }
];

const RESULT_STATUSES = [
  { value: 'Q', label: 'Q - Qualified' },
  { value: 'NQ', label: 'NQ - Not Qualified' },
  { value: 'ABS', label: 'ABS - Absent' },
  { value: 'DQ', label: 'DQ - Disqualified' },
  { value: 'EXC', label: 'EXC - Excused' }
];

const ScentworkRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  // Form fields
  const [trialDate, setTrialDate] = useState<string>(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [level, setLevel] = useState<string>(editRun?.level || "Novice A");
  const [element, setElement] = useState<string>(editRun?.element || "Container");
  const [resultStatus, setResultStatus] = useState<string>(editRun?.result_status || "Q");
  const [eventName, setEventName] = useState<string>(editRun?.event_name || "");
  const [city, setCity] = useState<string>(editRun?.location?.city || "");
  const [state, setState] = useState<string>(editRun?.location?.state || "");
  const [notes, setNotes] = useState<string>(editRun?.notes || "");

  // Validation errors
  const [errors, setErrors] = useState<any>({});

  const validate = () => {
    const newErrors: any = {};

    if (!trialDate) {
      newErrors.trialDate = "Trial date is required";
    } else if (new Date(trialDate) > new Date()) {
      newErrors.trialDate = "Trial date cannot be in the future";
    }

    if (!level) {
      newErrors.level = "Level is required";
    }

    if (!element) {
      newErrors.element = "Element is required";
    }

    // Validate level/element compatibility
    if (element === 'Detective' && level !== 'Detective') {
      newErrors.element = "Detective element can only be used with Detective level";
    }
    if (level === 'Detective' && element !== 'Detective') {
      newErrors.level = "Detective level can only be used with Detective element";
    }

    if (!resultStatus) {
      newErrors.resultStatus = "Result status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);

    const formData: any = {
      dogId: dog._id,
      trialDate: localDate,
      level,
      element,
      resultStatus,
      qualified: resultStatus === 'Q'
    };

    // Optional fields
    if (eventName.trim()) {
      formData.eventName = eventName.trim();
    }

    if (city.trim() || state.trim()) {
      formData.location = {};
      if (city.trim()) formData.location.city = city.trim();
      if (state.trim()) formData.location.state = state.trim();
    }

    if (notes.trim()) {
      formData.notes = notes.trim();
    }

    onSubmit(formData);
  };

  return (
    <form className="scentworkRunForm" onSubmit={handleSubmit}>
      <div className="formGrid">
        {/* Trial Date */}
        <div className="formField">
          <label htmlFor="trialDate">Trial Date *</label>
          <input
            id="trialDate"
            type="date"
            value={trialDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrialDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
          {errors.trialDate && <span className="error">{errors.trialDate}</span>}
        </div>

        {/* Level */}
        <div className="formField">
          <label htmlFor="level">Level *</label>
          <select
            id="level"
            value={level}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLevel(e.target.value)}
          >
            {LEVELS.map((lvl) => (
              <option key={lvl.value} value={lvl.value}>
                {lvl.label}
              </option>
            ))}
          </select>
          {errors.level && <span className="error">{errors.level}</span>}
        </div>

        {/* Element */}
        <div className="formField">
          <label htmlFor="element">Element *</label>
          <select
            id="element"
            value={element}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setElement(e.target.value)}
          >
            {ELEMENTS.map((elem) => (
              <option key={elem.value} value={elem.value}>
                {elem.label}
              </option>
            ))}
          </select>
          {errors.element && <span className="error">{errors.element}</span>}
          <span className="helpText">
            Note: Detective is a separate class - level and element must both be "Detective"
          </span>
        </div>

        {/* Result Status */}
        <div className="formField">
          <label htmlFor="resultStatus">Result *</label>
          <select
            id="resultStatus"
            value={resultStatus}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setResultStatus(e.target.value)}
          >
            {RESULT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          {errors.resultStatus && <span className="error">{errors.resultStatus}</span>}
        </div>

        {/* Event Name */}
        <div className="formField fullWidth">
          <label htmlFor="eventName">Event Name</label>
          <Input
            id="eventName"
            value={eventName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventName(e.target.value)}
            placeholder="e.g., Spring Scent Work Trial"
          />
        </div>

        {/* Location - City */}
        <div className="formField">
          <label htmlFor="city">City</label>
          <Input
            id="city"
            value={city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            placeholder="e.g., Denver"
          />
        </div>

        {/* Location - State */}
        <div className="formField">
          <label htmlFor="state">State</label>
          <Input
            id="state"
            value={state}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value)}
            placeholder="e.g., CO"
            maxLength={2}
          />
        </div>

        {/* Notes */}
        <div className="formField fullWidth">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
            placeholder="Additional notes about this run..."
            rows={3}
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="formActions">
        <Button
          onClick={onCancel}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : editRun ? "Update Run" : "Add Run"}
        </Button>
      </div>
    </form>
  );
};

export default ScentworkRunForm;

